import React from 'react';
import { Typography, Empty, Spin } from 'antd';
import { PageContainer } from '@ant-design/pro-layout';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_PENDING_APPROVALS } from 'gql/pendingApprovals';
import fp from 'lodash/fp';
import { DateDisplay, ProTable } from 'components/layout';
import './style.less';
import { ENUM_REPORT_TYPE } from 'constants/form';

const mapApplicationType = (type, data) => {
  switch (type) {
    case 'extensions':
      return 'Extension';
    case 'proposalRevisions':
      return 'Proposal Revision';
    case 'withdrawals':
      return 'Withdrawal';
    case 'virements':
      return 'Virement';
    case 'reportSubmissions':
      return ENUM_REPORT_TYPE[data.reportType] ?? 'Report Submission';
    default:
      return null;
  }
};
const mapApplicationUrl = (type, data) => {
  const { id, project } = data;

  switch (type) {
    case 'extensions':
      return `/projects/${project?.id}/extensions/${id}`;
    case 'proposalRevisions':
      return `/projects/${project?.id}/proposal-revisions/${id}`;
    case 'withdrawals':
      return `/projects/${project?.id}/withdrawals/${id}`;
    case 'virements':
      return `/projects/${project?.id}/budget-virement/virements/${id}`;
    case 'reportSubmissions':
      return `/projects/${project?.id}/report-submissions/${id}`;
    case 'projectApplications':
      return `/project-applications/${id}`;
    default:
      return null;
  }
};

const getApplications = (type) =>
  fp.pipe(
    fp.get('myPendingApprovals'),
    fp.get(type),
    fp.filter(Boolean),
    fp.filter(({ project }) => {
      return project?.projectStatus?.controlType === 'IN_PROGRESS';
    }),
    fp.map((data) => ({
      ...data,
      applicationType: mapApplicationType(type, data),
      url: mapApplicationUrl(type, data),
    }))
  );
const getExtensions = getApplications('extensions');
const getProposalRevisions = getApplications('proposalRevisions');
const getWithdrawals = getApplications('withdrawals');
const getVirements = getApplications('virements');
const getReportSubmissions = getApplications('reportSubmissions');
const getProjectApplications = fp.pipe(
  fp.get('projectApplications'),
  fp.get('nodes'),
  fp.map((data) => ({
    ...data,
    applicationType: 'Project Application',
    url: mapApplicationUrl('projectApplications', data),
    date: data.firstSubmissionDate,
    project: {
      title: data.title,
      profile: {
        department: data.hostDepartment,
        projectCode: data.refNumber,
      },
    },
  }))
);
const getData = (data) =>
  data
    ? [
        ...getExtensions(data),
        ...getProposalRevisions(data),
        ...getWithdrawals(data),
        ...getVirements(data),
        ...getReportSubmissions(data),
        ...getProjectApplications(data),
      ]
    : [];
const getFirst10 = fp.pipe(getData, fp.orderBy('date', 'desc'), fp.take(10));

const { Text } = Typography;

const columns = [
  {
    title: 'Application Type',
    dataIndex: 'applicationType',
    key: 'applicationType',
    render: (_, { url }) => <Link to={url}>{_}</Link>,
  },
  {
    title: 'Department',
    dataIndex: ['project', 'profile', 'department', 'abbreviation'],
    key: 'department',
    render: (_, { url }) => <Link to={url}>{_}</Link>,
  },
  {
    title: 'Project Code / Application Ref. Code',
    dataIndex: ['project', 'profile', 'projectCode'],
    key: 'projectCode',
    render: (_, { url }) => <Link to={url}>{_}</Link>,
  },
  {
    title: 'Project Title',
    dataIndex: ['project', 'title'],
    key: 'title',
    render: (_, { url }) => <Link to={url}>{_}</Link>,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    render: (_, { url }) => (
      <Link to={url}>
        <DateDisplay date={_} />
      </Link>
    ),
  },
];

const locale = {
  emptyText: (
    <Empty
      description="No pending approvals"
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  ),
};

const PendingApprovals = () => {
  const { data, loading } = useQuery(GET_PENDING_APPROVALS, {
    variables: {
      ignoreNotAuthorised: true,
    },
    errorPolicy: 'all',
  });
  const dataSource = getFirst10(data);

  return (
    <Spin spinning={loading}>
      <PageContainer ghost header={{ title: 'Pending Approvals' }}>
        {loading ? null : dataSource.length === 0 ? (
          <Text type="secondary">No record</Text>
        ) : (
          <>
            <Text type="secondary">
              The latest 10 pending approvals are shown,{' '}
            </Text>
            <Link to="/pending-approvals#extensions">click to see all</Link>
            <ProTable
              rowKey="id"
              locale={locale}
              className="pro-table-pending-approvals"
              dataSource={dataSource}
              loading={loading}
              columns={columns}
              search={false}
              toolBarRender={false}
              pagination={false}
              style={{ marginTop: '1rem' }}
              emptyText="Test"
            />
          </>
        )}
      </PageContainer>
    </Spin>
  );
};

export default PendingApprovals;
